import type { FrontNavigationConfigItem } from '@dts/shared/src/types/frontNavigation';
import type { RouteRecordRaw } from 'vue-router';

export interface FrontNavigationConfigMap {
  [key: string]: FrontNavigationConfigItem;
}

export interface FrontNavigationMenuNode {
  key: string;
  label: string;
  isNew: boolean;
  sort: number;
  defaultOrder: number;
  children?: FrontNavigationMenuNode[];
}

export const buildFrontNavigationConfigMap = (
  items: FrontNavigationConfigItem[] = [],
): FrontNavigationConfigMap => {
  return items.reduce<FrontNavigationConfigMap>((result, item) => {
    if (item?.navKey) {
      result[item.navKey] = item;
    }
    return result;
  }, {});
};

const getRouteKey = (route: RouteRecordRaw) => {
  return route.name ? String(route.name) : '';
};

const getFallbackLabel = (route: RouteRecordRaw, depth: number) => {
  const routeKey = getRouteKey(route);
  if (!routeKey) {
    return '';
  }
  return depth === 0 ? routeKey : String(route.meta?.title || routeKey);
};

export const buildFrontNavigationMenuTree = (
  routes: RouteRecordRaw[] = [],
  configMap: FrontNavigationConfigMap = {},
  depth = 0,
): FrontNavigationMenuNode[] => {
  return routes
    .filter((route) => !route.meta?.hide && route.name)
    .map((route, index) => {
      const routeKey = getRouteKey(route);
      const config = routeKey ? configMap[routeKey] : undefined;
      const children = route.children?.length
        ? buildFrontNavigationMenuTree(route.children, configMap, depth + 1)
        : undefined;

      return {
        key: routeKey,
        label: config?.navName || getFallbackLabel(route, depth),
        isNew: config ? Boolean(config.isNew) : Boolean(route.meta?.isNew),
        sort: config?.sort ?? index + 1,
        defaultOrder: index,
        children: children?.length ? children : undefined,
      };
    })
    .sort((left, right) => left.sort - right.sort || left.defaultOrder - right.defaultOrder);
};

export const hasNewBadge = (node: FrontNavigationMenuNode): boolean => {
  return node.isNew || Boolean(node.children?.some((child) => hasNewBadge(child)));
};
